// Reusable section components for the BestRx authority pages (white aesthetic).
// Pricing cards, No-Insurance band, How-It-Works, Request-Callback CTA, FAQ schema helper.
import { useState } from "react";
import { useLeadModal } from "@/components/shared/LeadProvider";
import {
  Shield,
  ShieldCheck,
  Phone,
  ArrowRight,
  ClipboardList,
  PhoneCall,
  Stethoscope,
  Package,
  Check,
  Star,
  Clock,
  Tag,
  Heart,
  Truck,
} from "lucide-react";

export const RED = "#E11D2A";
export const BLACK = "#0A0A0A";
const PHONE = "844-703-1780";
const TEL = "tel:+18447031780";

/* ─────────  PRICING CARDS  ───────── */
export function PricingCards({ slug, packages = [] }) {
  const { open } = useLeadModal();
  if (!packages.length) return null;
  return (
    <section className="bg-white py-14 md:py-20" data-testid={`pricing-${slug}`} id="pricing">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-10">
          <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px] md:text-[13px]" style={{ color: RED }}>
            TRANSPARENT PRICING
          </p>
          <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 4vw, 56px)" }}>
            STARTING AS LOW AS <span style={{ color: RED }}>$35</span> WEEK 1<span style={{ color: RED }}>*</span>
          </h2>
          <p className="mt-3 text-[14px] md:text-[15px] text-black/65 max-w-[640px] mx-auto">
            Three programs, every price shown up front. No insurance required.
            <br className="hidden md:block" />
            New customers always get the first week for $35.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {packages.map((p, i) => {
            const featured = p.badge === "BEST VALUE";
            return (
              <div
                key={p.tier}
                data-testid={`price-${slug}-${p.tier.toLowerCase()}`}
                className={`relative rounded-lg border-2 bg-white p-7 md:p-8 flex flex-col ${featured ? "scale-[1.03]" : ""}`}
                style={{
                  borderColor: featured ? RED : "rgba(0,0,0,0.1)",
                  boxShadow: featured
                    ? "0 0 0 6px rgba(225,29,42,0.06), 0 24px 50px rgba(225,29,42,0.20)"
                    : "0 8px 20px rgba(0,0,0,0.05)",
                }}
              >
                {p.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-white text-[10.5px] font-black tracking-[0.18em] whitespace-nowrap"
                    style={{ background: featured ? RED : BLACK }}
                  >
                    {p.badge}
                  </div>
                )}
                <p className="font-heavy text-[18px] md:text-[20px] text-black mb-1 mt-2">{p.tier}</p>
                <p className="text-[12.5px] tracking-wider text-black/55 mb-5 font-bold">{p.duration}</p>
                <div className="mb-4">
                  <span className="font-heavy" style={{ color: RED, fontSize: "clamp(44px, 4.6vw, 60px)" }}>
                    ${p.price}
                  </span>
                  <span className="text-black/55 font-bold ml-1">/ {p.unit || "month"}</span>
                </div>
                <p className="text-[13px] text-black/70 leading-relaxed mb-5">{p.description}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.includes.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[13px] text-black/80">
                      <Check size={16} color={RED} strokeWidth={3} className="shrink-0 mt-0.5" />
                      {it}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => open({ source: `pricing_${slug}_${p.tier}` })}
                  data-testid={`price-${slug}-${p.tier.toLowerCase()}-cta`}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 font-extrabold tracking-[0.08em] uppercase text-[13px] rounded-sm transition-transform hover:-translate-y-0.5"
                  style={
                    featured
                      ? { background: RED, color: "white", boxShadow: "0 10px 24px rgba(225,29,42,0.30)" }
                      : { background: "white", color: BLACK, border: `2px solid ${BLACK}` }
                  }
                >
                  Start Now <ArrowRight size={16} strokeWidth={2.6} />
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-[11.5px] text-black/55">
          *Introductory offer for new customers; provider review required. Continuation pricing applies after week 1.
        </p>
      </div>
    </section>
  );
}

/* ─────────  NO INSURANCE BAND  ───────── */
export function NoInsuranceSection() {
  const { open } = useLeadModal();
  return (
    <section
      className="relative bg-black text-white py-16 md:py-24 overflow-hidden"
      data-testid="no-insurance-section"
      id="no-insurance"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{ background: `radial-gradient(circle at 30% 50%, ${RED} 0%, transparent 55%)` }}
      />
      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="font-extrabold tracking-[0.18em] mb-3 text-[12px]" style={{ color: RED }}>
            NO INSURANCE REQUIRED
          </p>
          <h2 className="font-heavy leading-[0.95] mb-5" style={{ fontSize: "clamp(34px, 5.6vw, 84px)" }}>
            MOST PEOPLE THINK<br />
            THEY NEED <span style={{ color: RED }}>INSURANCE.</span><br />
            THEY <span style={{ color: RED }}>DON'T.</span>
          </h2>
          <p className="text-[15px] md:text-[17px] text-white/80 leading-relaxed max-w-[640px] mb-7">
            BestRx is a flat-priced, cash-pay platform. No copays. No deductibles. No claim denials.
            For most patients, our monthly program costs less than what insurance would charge after deductibles —
            and far less than concierge clinics charging $500+ per visit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => open({ source: "no_insurance_section" })}
              data-testid="no-insurance-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 font-extrabold tracking-[0.08em] uppercase text-white text-[14px] transition-transform hover:-translate-y-0.5"
              style={{ background: RED, boxShadow: "0 14px 30px rgba(225,29,42,0.40)" }}
            >
              Start Now <ArrowRight size={18} strokeWidth={2.6} />
            </button>
            <a
              href={TEL}
              data-testid="no-insurance-phone"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 font-extrabold tracking-[0.08em] uppercase text-white text-[14px] border-2 border-white"
            >
              <Phone size={18} /> Call {PHONE}
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: Tag,         t: "FLAT MONTHLY", s: "Same price every month. Cancel anytime." },
              { Icon: ShieldCheck, t: "NO CLAIMS",     s: "No insurance paperwork. Ever." },
              { Icon: Clock,       t: "FASTER",        s: "Approved in 24 hrs, shipped in 3–5 days." },
              { Icon: Heart,       t: "FAIR",          s: "Same protocols as $500/visit clinics." },
            ].map(({ Icon, t, s }) => (
              <div key={t} className="border border-white/15 rounded-md p-5" style={{ background: "rgba(255,255,255,0.04)" }}>
                <Icon size={26} color={RED} strokeWidth={2.2} className="mb-3" />
                <p className="font-heavy text-[14px] text-white tracking-tight mb-1.5">{t}</p>
                <p className="text-[12px] text-white/65 leading-snug">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────  HOW IT WORKS (4 STEPS)  ───────── */
export function HowItWorks4() {
  const steps = [
    { n: 1, Icon: ClipboardList, t: "REQUEST INFORMATION", s: "Tell us a few basics. Takes 60 seconds." },
    { n: 2, Icon: PhoneCall,     t: "WE CALL YOU",         s: "Within 30 minutes during business hours." },
    { n: 3, Icon: Stethoscope,   t: "PROVIDER REVIEW",     s: "A licensed Texas clinician reviews your file." },
    { n: 4, Icon: Package,       t: "MEDICATION SHIPS",    s: "Discreet shipping in 3–5 business days." },
  ];
  return (
    <section className="bg-white py-14 md:py-20" data-testid="how-it-works" id="how-it-works">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-10 md:mb-14">
          <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px] md:text-[13px]" style={{ color: RED }}>
            SIMPLE. FAST. PRIVATE.
          </p>
          <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 4vw, 56px)" }}>
            HOW IT <span style={{ color: RED }}>WORKS</span>.
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
          {steps.map(({ n, Icon, t, s }, idx) => (
            <div key={n} className="relative" data-testid={`hiw-step-${n}`}>
              <div
                className="relative flex flex-col items-start p-6 rounded-md border bg-white"
                style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 6px 16px rgba(0,0,0,0.04)" }}
              >
                <div
                  className="absolute -top-3 -left-3 w-9 h-9 rounded-full flex items-center justify-center font-heavy text-white text-[14px]"
                  style={{ background: RED, boxShadow: "0 6px 12px rgba(225,29,42,0.4)" }}
                >
                  {n}
                </div>
                <Icon size={36} strokeWidth={1.8} className="text-black mb-4 mt-2" />
                <p className="font-heavy text-[13px] md:text-[14px] tracking-tight text-black mb-1.5">{t}</p>
                <p className="text-[12px] text-black/60 leading-snug">{s}</p>
              </div>
              {idx < steps.length - 1 && (
                <ArrowRight size={22} strokeWidth={2.4} className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 text-black/40" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────  REQUEST A CALLBACK  ───────── */
export function RequestCallbackCTA({ source = "callback" }) {
  const { open } = useLeadModal();
  return (
    <section className="bg-white py-12 md:py-16" data-testid="callback-cta">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
        <div
          className="rounded-md border-2 p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 items-center gap-6"
          style={{ borderColor: RED, background: "white", boxShadow: "0 0 0 6px rgba(225,29,42,0.06), 0 22px 40px rgba(225,29,42,0.10)" }}
        >
          <div className="md:col-span-7">
            <p className="font-extrabold tracking-[0.18em] mb-2 text-[11.5px]" style={{ color: RED }}>
              WE WILL CALL YOU WITHIN 30 MINUTES DURING BUSINESS HOURS
            </p>
            <h3 className="font-heavy leading-tight" style={{ fontSize: "clamp(22px, 2.8vw, 36px)" }}>
              CAN YOU WAIT A WEEK? THEN WHY <span style={{ color: RED }}>PAY MORE?</span>
            </h3>
            <p className="mt-3 text-[14px] text-black/70 max-w-[620px]">
              Talk to a real human. No bots, no insurance paperwork. We answer questions, walk you through pricing, and start your assessment in minutes.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <button
              onClick={() => open({ source })}
              data-testid={`callback-start-${source}`}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-extrabold tracking-[0.08em] uppercase text-white text-[14px] rounded-sm transition-transform hover:-translate-y-0.5"
              style={{ background: RED, boxShadow: "0 14px 30px rgba(225,29,42,0.30)" }}
            >
              Request a Call <ArrowRight size={18} strokeWidth={2.6} />
            </button>
            <a
              href={TEL}
              data-testid={`callback-phone-${source}`}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-extrabold tracking-[0.08em] uppercase text-[14px] rounded-sm border-2 border-black text-black"
            >
              <Phone size={18} /> Call {PHONE}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────  WHY BESTRX  ───────── */
export function WhyBestRx() {
  const items = [
    { Icon: Tag,         t: "BETTER PRICES",        s: "Flat, transparent monthly pricing. Always shown up front." },
    { Icon: Stethoscope, t: "PROVIDER REVIEWED",    s: "Every prescription cleared by a Texas-licensed clinician." },
    { Icon: ShieldCheck, t: "NO INSURANCE NEEDED",  s: "Cash-pay. No copays, no deductibles, no claim denials." },
    { Icon: Star,        t: "SIMPLE PROCESS",       s: "Four-minute assessment. Provider call. Medication ships." },
    { Icon: Truck,       t: "DELIVERED TO YOUR DOOR", s: "Discreet, refrigerated shipping anywhere in Texas." },
  ];
  return (
    <section className="bg-white py-14 md:py-20" data-testid="why-bestrx" id="why">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-10 md:mb-14">
          <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px] md:text-[13px]" style={{ color: RED }}>
            WHY BESTRX
          </p>
          <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 4vw, 56px)" }}>
            BETTER QUALITY. BETTER PRICES. <span style={{ color: RED }}>BETTER EXPERIENCE.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {items.map(({ Icon, t, s }) => (
            <div key={t} className="p-5 md:p-6 rounded-md border bg-white" style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 6px 14px rgba(0,0,0,0.04)" }}>
              <Icon size={28} strokeWidth={2} color={RED} className="mb-3" />
              <p className="font-heavy text-[13px] md:text-[14px] tracking-tight text-black mb-1.5">{t}</p>
              <p className="text-[12px] text-black/60 leading-snug">{s}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────  WHO IS THIS FOR  ───────── */
export function WhoIsFor({ items = [] }) {
  if (!items.length) return null;
  return (
    <section className="bg-white py-14 md:py-20" data-testid="who-for">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="mb-10">
          <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px] md:text-[13px]" style={{ color: RED }}>
            IS THIS YOU?
          </p>
          <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 4vw, 56px)" }}>
            WHO IS THIS <span style={{ color: RED }}>FOR?</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {items.map(({ Icon, label }) => (
            <div
              key={label}
              className="p-5 rounded-md text-center bg-white border"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
              data-testid={`who-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {Icon && <Icon size={28} strokeWidth={2} className="text-black mb-2 mx-auto" />}
              <p className="font-heavy text-[12.5px] tracking-tight text-black uppercase">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────  FAQ ACCORDION  ───────── */
export function FAQAccordion({ items = [], title = "Frequently Asked Questions" }) {
  const [openIdx, setOpenIdx] = useState(0);
  if (!items.length) return null;
  return (
    <section className="bg-white py-14 md:py-20 border-t border-black/10" data-testid="faq-section" id="faq">
      <div className="max-w-[920px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-10">
          <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px] md:text-[13px]" style={{ color: RED }}>
            FAQ
          </p>
          <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 4vw, 52px)" }}>
            {title}
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((f, i) => (
            <div
              key={i}
              className="rounded-md border bg-white overflow-hidden"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
              data-testid={`faq-${i}`}
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-black/[0.02]"
                aria-expanded={openIdx === i}
              >
                <span className="font-extrabold text-[14px] md:text-[15px] text-black tracking-tight">{f.q}</span>
                <span
                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-black text-[16px]"
                  style={{ background: openIdx === i ? RED : "transparent", color: openIdx === i ? "white" : RED, border: `2px solid ${RED}` }}
                >
                  {openIdx === i ? "−" : "+"}
                </span>
              </button>
              {openIdx === i && (
                <div className="px-5 pb-5 text-[14px] text-black/70 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────  RELATED PAGES (authority cluster)  ───────── */
export function RelatedCluster({ medications = [], cities = [], currentMed, currentCity }) {
  return (
    <section className="bg-[#FAFAFA] py-14 md:py-20 border-y border-black/10" data-testid="related-cluster">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        {medications.length > 0 && (
          <div>
            <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px]" style={{ color: RED }}>
              EXPLORE MEDICATIONS
            </p>
            <h3 className="font-heavy leading-tight mb-5" style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}>
              MORE PROGRAMS
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {medications
                .filter((m) => m.slug !== currentMed)
                .slice(0, 10)
                .map((m) => (
                  <li key={m.slug}>
                    <a
                      href={`/medications/${m.slug}`}
                      className="block px-4 py-3 rounded-md border bg-white text-[13px] font-extrabold text-black hover:border-red-600 hover:text-red-600 transition-colors"
                      style={{ borderColor: "rgba(0,0,0,0.1)" }}
                      data-testid={`related-med-${m.slug}`}
                    >
                      {m.name}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        )}
        {cities.length > 0 && (
          <div>
            <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px]" style={{ color: RED }}>
              SERVING TEXAS
            </p>
            <h3 className="font-heavy leading-tight mb-5" style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}>
              CITIES WE SERVE
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {cities
                .filter((c) => c.slug !== currentCity)
                .map((c) => (
                  <li key={c.slug}>
                    <a
                      href={`/locations/${c.slug}`}
                      className="block px-4 py-3 rounded-md border bg-white text-[13px] font-extrabold text-black hover:border-red-600 hover:text-red-600 transition-colors"
                      style={{ borderColor: "rgba(0,0,0,0.1)" }}
                      data-testid={`related-city-${c.slug}`}
                    >
                      BestRx {c.name}, {c.state}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────  FAQ → JSON-LD SCHEMA  ───────── */
export const faqSchemaFrom = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
